import React, { useState, useEffect } from 'react'
import { token } from '@atlaskit/tokens'
import Avatar from '@atlaskit/avatar'
import { formatRelativeTime } from '../../utils/formatters'
import { getUserDetails, getCachedUserDetails } from '../../utils/userCache'

/**
 * FIELD NAME MAPPINGS
 * Converts API field names to human-readable display names.
 * Separate mappings for test cases and test steps.
 */
const TEST_CASE_FIELD_NAMES = {
  testCaseName: 'Test Case Name',
  description: 'Description',
  priority: 'Priority',
  assigneeId: 'Assignee'
}

const TEST_STEP_FIELD_NAMES = {
  stepAction: 'Step Action',
  testData: 'Test Data',
  expectedResult: 'Expected Result'
}

/**
 * LONG TEXT FIELDS
 * Fields that should use block-based display instead of inline format.
 * These fields typically contain multi-line or lengthy text content.
 */
const LONG_TEXT_FIELDS = [
  'description',
  'stepAction',
  'testData',
  'expectedResult',
  'testCaseName'
]

/**
 * IS LONG TEXT FIELD
 * Determines if a field should use block-based display.
 *
 * @param {string} fieldName - The API field name
 * @returns {boolean} True if field should use block display
 */
const isLongTextField = (fieldName) => {
  return LONG_TEXT_FIELDS.includes(fieldName)
}

/**
 * GROUP ENTRIES BY TIMESTAMP
 * Groups audit entries that occurred within 1 second of each other
 * and by the same user, indicating they were part of the same save operation.
 *
 * @param {Array} entries - Array of audit log entries from API
 * @returns {Array<Array>} Array of grouped entries
 */
export function groupEntriesByTimestamp(entries) {
  if (!entries || entries.length === 0) return []

  const groups = []
  let currentGroup = []

  // Sort by changedOn descending (newest first)
  const sorted = [...entries].sort((a, b) =>
    new Date(b.changedOn) - new Date(a.changedOn)
  )

  sorted.forEach((entry, index) => {
    if (index === 0) {
      currentGroup.push(entry)
      return
    }

    const prevTime = new Date(sorted[index - 1].changedOn).getTime()
    const currentTime = new Date(entry.changedOn).getTime()

    // If within 1 second and same user, group together
    if (
      Math.abs(prevTime - currentTime) <= 1000 &&
      entry.changedByAccountId === sorted[index - 1].changedByAccountId
    ) {
      currentGroup.push(entry)
    } else {
      groups.push(currentGroup)
      currentGroup = [entry]
    }
  })

  if (currentGroup.length > 0) {
    groups.push(currentGroup)
  }

  return groups
}

/**
 * FIELD CHANGE MESSAGE COMPONENT
 * Renders the change message for a single field update.
 * Handles special cases like assignee field (resolves account IDs).
 *
 * @param {object} entry - Single audit log entry
 * @param {string} entityType - 'testCase' or 'testStep'
 */
function FieldChangeMessage({ entry, entityType }) {
  // State for assignee names (for assigneeId field changes)
  const [oldAssigneeName, setOldAssigneeName] = useState(null)
  const [newAssigneeName, setNewAssigneeName] = useState(null)

  // Get field display name based on entity type
  const fieldMappings = entityType === 'testCase'
    ? TEST_CASE_FIELD_NAMES
    : TEST_STEP_FIELD_NAMES
  const fieldDisplayName = fieldMappings[entry.fieldName] || entry.fieldName

  // Fetch assignee names if this is an assignee change
  useEffect(() => {
    if (entry?.fieldName === 'assigneeId') {
      if (entry.oldValue) {
        getUserDetails(entry.oldValue).then(details => {
          setOldAssigneeName(details?.displayName || entry.oldValue)
        })
      }
      if (entry.newValue) {
        getUserDetails(entry.newValue).then(details => {
          setNewAssigneeName(details?.displayName || entry.newValue)
        })
      }
    }
  }, [entry?.fieldName, entry?.oldValue, entry?.newValue])

  // Special handling for assignee field - use inline format
  if (entry.fieldName === 'assigneeId') {
    return (
      <div
        style={{
          fontSize: '13px',
          color: token('color.text', '#172B4D'),
          lineHeight: '1.5',
          marginBottom: '4px'
        }}
      >
        Changed <strong>{fieldDisplayName}</strong>
        {entry.oldValue && (
          <>
            {' from '}
            <span
              style={{
                textDecoration: 'line-through',
                color: token('color.text.subtle', '#5E6C84')
              }}
            >
              {oldAssigneeName || 'Loading...'}
            </span>
          </>
        )}
        {entry.newValue && (
          <>
            {' to '}
            <span style={{ fontWeight: 500 }}>
              {newAssigneeName || 'Loading...'}
            </span>
          </>
        )}
        {!entry.newValue && entry.oldValue && ' (unassigned)'}
        {!entry.newValue && !entry.oldValue && ' (set to unassigned)'}
      </div>
    )
  }

  // For short text fields like Priority, use inline format
  if (!isLongTextField(entry.fieldName)) {
    return (
      <div
        style={{
          fontSize: '13px',
          color: token('color.text', '#172B4D'),
          lineHeight: '1.5',
          marginBottom: '4px'
        }}
      >
        Changed <strong>{fieldDisplayName}</strong>
        {entry.oldValue && (
          <>
            {' from '}
            <span
              style={{
                textDecoration: 'line-through',
                color: token('color.text.subtle', '#5E6C84')
              }}
            >
              {entry.oldValue}
            </span>
          </>
        )}
        {entry.newValue && (
          <>
            {' to '}
            <span style={{ fontWeight: 500 }}>
              {entry.newValue}
            </span>
          </>
        )}
        {!entry.newValue && entry.oldValue && ' (cleared)'}
      </div>
    )
  }

  // For long text fields (Description, Step Action, Test Data, Expected Result)
  // Use Jira-style block format showing full text
  return (
    <div
      style={{
        fontSize: '13px',
        color: token('color.text', '#172B4D'),
        lineHeight: '1.5',
        marginBottom: '8px'
      }}
    >
      {/* Field name header */}
      <div style={{ marginBottom: '8px' }}>
        Changed <strong>{fieldDisplayName}</strong>
      </div>

      {/* Old value block - red accent */}
      {entry.oldValue && (
        <div
          style={{
            backgroundColor: token('color.background.danger', '#FFEDEB'),
            borderLeft: `3px solid ${token('color.border.danger', '#FF5630')}`,
            padding: '8px 12px',
            borderRadius: '3px',
            marginBottom: '8px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          <div
            style={{
              fontSize: '11px',
              fontWeight: 500,
              color: token('color.text.subtle', '#5E6C84'),
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            Previous
          </div>
          <div
            style={{
              color: token('color.text.subtle', '#5E6C84'),
              fontSize: '13px',
              lineHeight: '1.5'
            }}
          >
            {entry.oldValue}
          </div>
        </div>
      )}

      {/* New value block - green accent */}
      {entry.newValue && (
        <div
          style={{
            backgroundColor: token('color.background.success', '#E3FCEF'),
            borderLeft: `3px solid ${token('color.border.success', '#36B37E')}`,
            padding: '8px 12px',
            borderRadius: '3px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          <div
            style={{
              fontSize: '11px',
              fontWeight: 500,
              color: token('color.text.subtle', '#5E6C84'),
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            New
          </div>
          <div
            style={{
              color: token('color.text', '#172B4D'),
              fontSize: '13px',
              lineHeight: '1.5'
            }}
          >
            {entry.newValue}
          </div>
        </div>
      )}

      {/* Cleared message when value is removed */}
      {!entry.newValue && entry.oldValue && (
        <div
          style={{
            fontSize: '13px',
            color: token('color.text.subtle', '#5E6C84'),
            fontStyle: 'italic'
          }}
        >
          (cleared)
        </div>
      )}
    </div>
  )
}

/**
 * GROUPED AUDIT ITEM COMPONENT
 * Renders a single grouped audit entry (may contain multiple field changes).
 * Shows CREATED action as single message, UPDATED action lists each field change.
 *
 * @param {Array} entries - Array of audit entries in this group
 * @param {string} entityType - 'testCase' or 'testStep'
 */
function GroupedAuditItem({ entries, entityType }) {
  // All entries in group share same user and approximate timestamp
  const firstEntry = entries[0]

  // State for user details (who made the change)
  const [userDetails, setUserDetails] = useState(() =>
    getCachedUserDetails(firstEntry?.changedByAccountId)
  )

  // Fetch user who made the change
  useEffect(() => {
    if (firstEntry?.changedByAccountId) {
      getUserDetails(firstEntry.changedByAccountId).then(details => {
        if (details) setUserDetails(details)
      })
    }
  }, [firstEntry?.changedByAccountId])

  // Determine display name and avatar
  const displayName = userDetails?.displayName || 'Unknown User'
  const avatarUrl = userDetails?.avatarUrl || null

  // Check if group contains CREATED action
  const hasCreated = entries.some(e => e.action === 'CREATED')
  // Check if group contains DELETED action
  const hasDeleted = entries.some(e => e.action === 'DELETED')

  // Get entity display name for messages
  const entityDisplayName = entityType === 'testCase' ? 'test case' : 'test step'

  return (
    <div
      className="field-change-audit-log-item"
      style={{
        padding: '12px',
        borderRadius: '4px',
        backgroundColor: token('color.background.neutral', '#F4F5F7'),
        marginBottom: '8px'
      }}
    >
      {/* Header: Avatar + Name + Timestamp */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '6px'
        }}
      >
        {/* Left: Avatar + Name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Avatar size="small" src={avatarUrl} name={displayName} />
          <span
            style={{
              fontSize: '13px',
              fontWeight: 500,
              color: token('color.text', '#172B4D')
            }}
          >
            {displayName}
          </span>
        </div>

        {/* Right: Timestamp */}
        <span
          style={{
            fontSize: '12px',
            color: token('color.text.subtle', '#5E6C84')
          }}
        >
          {formatRelativeTime(firstEntry.changedOn)}
        </span>
      </div>

      {/* Changes */}
      <div style={{ paddingLeft: '32px' }}>
        {hasCreated ? (
          // Show single "Created" message for CREATED action
          <span
            style={{
              fontSize: '13px',
              color: token('color.text', '#172B4D')
            }}
          >
            Created this {entityDisplayName}
          </span>
        ) : hasDeleted ? (
          // Show single "Deleted" message for DELETED action
          <span
            style={{
              fontSize: '13px',
              color: token('color.text', '#172B4D')
            }}
          >
            Deleted this {entityDisplayName}
          </span>
        ) : (
          // Show individual field changes for UPDATED action
          entries.map((entry) => (
            <FieldChangeMessage
              key={entry.id}
              entry={entry}
              entityType={entityType}
            />
          ))
        )}
      </div>
    </div>
  )
}

/**
 * GROUPED AUDIT LOG ENTRIES COMPONENT
 * Main export component that renders audit entries grouped by timestamp.
 * Use this in modal tabs to display the audit log.
 *
 * @param {Array} entries - Array of audit log entries from API
 * @param {string} entityType - 'testCase' or 'testStep'
 */
export function GroupedAuditLogEntries({ entries, entityType }) {
  const groups = groupEntriesByTimestamp(entries)

  return (
    <>
      {groups.map((group, groupIndex) => (
        <GroupedAuditItem
          key={group[0]?.id || groupIndex}
          entries={group}
          entityType={entityType}
        />
      ))}
    </>
  )
}

/**
 * FIELD CHANGE AUDIT LOG ITEM COMPONENT
 * Displays a single audit log entry for field changes.
 * For ungrouped display - use GroupedAuditLogEntries for grouped display.
 *
 * @param {object} entry - Audit log entry from API
 * @param {string} entityType - 'testCase' or 'testStep'
 */
export function FieldChangeAuditLogItem({ entry, entityType }) {
  // State for user details (who made the change)
  const [userDetails, setUserDetails] = useState(() =>
    getCachedUserDetails(entry?.changedByAccountId)
  )

  // Fetch user who made the change
  useEffect(() => {
    if (entry?.changedByAccountId) {
      getUserDetails(entry.changedByAccountId).then(details => {
        if (details) setUserDetails(details)
      })
    }
  }, [entry?.changedByAccountId])

  // Determine display name and avatar
  const displayName = userDetails?.displayName || 'Unknown User'
  const avatarUrl = userDetails?.avatarUrl || null

  // Get entity display name for messages
  const entityDisplayName = entityType === 'testCase' ? 'test case' : 'test step'

  // Get action message based on action type
  const getActionContent = () => {
    switch (entry.action) {
      case 'CREATED':
        return (
          <span
            style={{
              fontSize: '13px',
              color: token('color.text', '#172B4D')
            }}
          >
            Created this {entityDisplayName}
          </span>
        )
      case 'DELETED':
        return (
          <span
            style={{
              fontSize: '13px',
              color: token('color.text', '#172B4D')
            }}
          >
            Deleted this {entityDisplayName}
          </span>
        )
      case 'UPDATED':
        return <FieldChangeMessage entry={entry} entityType={entityType} />
      default:
        return (
          <span
            style={{
              fontSize: '13px',
              color: token('color.text', '#172B4D')
            }}
          >
            {entry.action}
          </span>
        )
    }
  }

  return (
    <div
      className="field-change-audit-log-item"
      style={{
        padding: '12px',
        borderRadius: '4px',
        backgroundColor: token('color.background.neutral', '#F4F5F7'),
        marginBottom: '8px'
      }}
    >
      {/* Header: Avatar + Name + Timestamp */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '6px'
        }}
      >
        {/* Left: Avatar + Name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Avatar size="small" src={avatarUrl} name={displayName} />
          <span
            style={{
              fontSize: '13px',
              fontWeight: 500,
              color: token('color.text', '#172B4D')
            }}
          >
            {displayName}
          </span>
        </div>

        {/* Right: Timestamp */}
        <span
          style={{
            fontSize: '12px',
            color: token('color.text.subtle', '#5E6C84')
          }}
        >
          {formatRelativeTime(entry.changedOn)}
        </span>
      </div>

      {/* Action message */}
      <div style={{ paddingLeft: '32px' }}>
        {getActionContent()}
      </div>
    </div>
  )
}

export default FieldChangeAuditLogItem
